import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { passwordResetConfirm } from '../api/client';
import logo from '../logo.svg';

const MIN_PASSWORD_LENGTH = 8;

function ResetPasswordPage() {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const hasToken = Boolean(token);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!hasToken) {
      setErrorMessage('Reset link is missing or invalid.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await passwordResetConfirm(token, newPassword);
      setIsSuccess(true);
    } catch (error) {
      setErrorMessage('Could not reset password. The link may have expired.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page page-shell auth-page">
      <div className="public-shell">
        <section className="card public-panel">
          <div className="public-panel-copy">
            <div className="public-brand">
              <img src={logo} alt="Sumrise Logo" />
              <span>Sumrise</span>
            </div>
            <p className="public-kicker">Recovery</p>
            <h1>Reset Password</h1>
            <p>Set a fresh password for the account attached to this reset link.</p>
          </div>
          <ul className="public-feature-list">
            <li>
              <strong>Secure reset flow</strong>
              <p>Reset links are token-based and expire if they are no longer valid.</p>
            </li>
            <li>
              <strong>Login resumes after reset</strong>
              <p>Once the password is updated, the account can return to the normal sign-in flow.</p>
            </li>
          </ul>
        </section>

        <section className="card auth-card">
          <div className="auth-card-header">
            <p className="public-kicker">Choose a new password</p>
            <p className="muted-text">Use at least {MIN_PASSWORD_LENGTH} characters to update your credentials.</p>
          </div>
          {!hasToken ? <p role="status" className="notice">Reset link is missing or invalid.</p> : null}
          {isSuccess ? (
            <div className="stack-sm">
              <p role="status" className="notice notice-info">Password updated. Please log in.</p>
              <Link to="/login" className="btn btn-primary auth-action">Go to Login</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="stack-md">
              <div>
                <label htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              <div>
                <label htmlFor="confirm-new-password">Confirm New Password</label>
                <input
                  id="confirm-new-password"
                  name="confirm-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              <button type="submit" className="btn btn-primary auth-action" disabled={isSubmitting || !hasToken}>
                {isSubmitting ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}
          {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
          <div className="auth-card-footer">
            <p>
              <Link to="/login">Back to Login</Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
